import React from 'react';
import { X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const MobileBaseballPredictionBar = ({ 
  selectionCount, 
  usageState, 
  isLoading, 
  error,
  refundNotice,
  onPredict, 
  onClear,
  isButtonDisabled = false 
}) => {
  if (selectionCount === 0 && !error && !refundNotice) return null;

  const canPredict = usageState.canUse && selectionCount > 0 && !isLoading && !isButtonDisabled;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-14 left-0 right-0 z-50 p-3 pb-4 bg-black border-t border-white/10"
      >
        {/* Error Message */}
        {error && (
          <div className="mb-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}

        {/* Refund Notice */}
        {refundNotice && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-3 bg-emerald-500/[0.02] border border-emerald-500/20"
          >
            <p className="text-xs text-emerald-400/70 font-medium">
              {refundNotice}
            </p>
          </motion.div>
        )}

        {/* Selection Info */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="border-l border-violet-500/30 pl-3">
              <p className="text-sm font-medium text-white/95">
                {selectionCount} {selectionCount === 1 ? 'Game Selected' : 'Games Selected'}
              </p>
              <p className="text-xs text-white/50 font-mono">
                7 coins/game • {selectionCount * 7} coins total
              </p>
            </div>
          </div>
          
          <button
            onClick={onClear}
            className={`p-2 transition-all ${
              isLoading || isButtonDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-white/[0.02] cursor-pointer'
            }`}
            disabled={isLoading || isButtonDisabled}
          >
            <X size={18} className={isLoading || isButtonDisabled ? 'text-white/30' : 'text-white/50'} />
          </button>
        </div>

        {/* Action Button */}
        <button
          onClick={onPredict}
          disabled={!canPredict}
          className={`w-full flex items-center justify-center gap-2 px-6 py-2 font-medium text-sm transition-all backdrop-blur-sm ${
            canPredict
              ? 'bg-violet-500/20 text-white border border-violet-500/30 hover:bg-violet-500/30 hover:border-violet-500/50'
              : 'bg-white/[0.05] text-white/30 cursor-not-allowed border border-white/[0.05]'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Analyzing games</span>
            </>
          ) : isButtonDisabled ? (
            <>
              <Loader2 size={16} className="animate-spin opacity-50" />
              <span>Processing</span>
            </>
          ) : (
            <span>Predict Winners</span>
          )}
        </button>

        {/* Usage Warning */}
        {!usageState.canUse && selectionCount > 0 && (
          <p className="text-xs text-violet-400/70 text-center mt-2 font-mono">
            Insufficient Star Coins. Need {selectionCount * 7} coins.
          </p>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default MobileBaseballPredictionBar;